import { FC, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TieredMenu } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tag } from 'primereact/tag';
import { filterStateDiagnosticAssessmentsInitValue } from '../AssessmentHistory/models';
import { selectedDiagnosticReportsFilter } from '../../redux/reducers/filterDiagnosticReport';
import { DiagnosticReportByClass } from './DiagnosticReportSkillByClass';
import { Button } from 'primereact/button';
import { sortByLastThenFirst } from '../../businessLogicService/orderByLastName';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';

export interface DiagnosticSkillPerformanceItem {
  skill: SkillItem | null;
  placedOut: PlacedOut[];
}

interface PlacedOut {
  id: string;
  firstName: string;
  lastName: string;
  publicUserId: string;
}

interface SkillItem {
  id: string;
  code: string | null;
  name: string | null;
  displayType: string | null;
  displayUnit: string | null;
  moreCount: number;
  allUnitsByType?: Record<string, string[]>;
}

interface Props {
  reportFilters: ReturnType<typeof selectedDiagnosticReportsFilter>;
}

export type RowData = {
  skillId: string;
  skillName: string;
  type: string;
  unit: string;
  moreCount?: number;
  allUnitsByType?: Record<string, string[]>;
  PLACED_OUT: number;
  NOT_PLACED_OUT: number;
} & Record<string, string | number | Record<string, string[]> | undefined>;

export const SkillPerformanceByClassTable: FC<Props & { onTableData?: (rows: RowData[]) => void }> = ({
  reportFilters,
  onTableData,
}) => {
  const [skillAssessments, setSkillAssessments] = useState<DiagnosticSkillPerformanceItem[]>([]);
  const [placedOutMap, setPlacedOutMap] = useState<Record<string, PlacedOut[]>>({});
  const [classStudents, setClassStudents] = useState<PlacedOut[]>([]);
  const menuRef = useRef<TieredMenu>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const unitOverlayRef = useRef<OverlayPanel>(null);
  const [overlayUnits, setOverlayUnits] = useState<string[]>([]);

  const classIds = useMemo(() => reportFilters?.classes?.map(c => c.id) ?? [], [reportFilters]);
  const roadmapIds = useMemo(() => (reportFilters?.roadmap ? [reportFilters.roadmap.id] : []), [reportFilters]);
  useEffect(() => {
    setIsLoading(true);
  }, [classIds, roadmapIds]);
  const [filterState, setFilterState] = useState({
    ...filterStateDiagnosticAssessmentsInitValue,
    filters: {
      ...filterStateDiagnosticAssessmentsInitValue.filters,
      classes: classIds,
      roadmaps: roadmapIds,
      skillName: '',
      type: [] as string[],
      unit: '',
    },
    sortField: 'skillName',
    sortOrder: 1 as 1 | -1,
    page: 0,
    first: 0,
    rows: 10,
  });
  const [searchValue, setSearchValue] = useState(filterState.filters.skillName);
  const [selectType, setSelectType] = useState<string[]>(
    Array.isArray(filterState.filters.type) ? (filterState.filters.type as string[]) : [],
  );
  const [selectUnit, setSelectUnit] = useState(filterState.filters.unit);

  // Identity of the server-side filter set. Used as `key` on DiagnosticReportByClass
  // so it fully remounts (resetting its internal pagination) whenever these change —
  // the only reliable way to reset that state given its queries suspend on change.
  const skillReportKey = useMemo(
    () =>
      JSON.stringify({
        classes: filterState.filters.classes,
        roadmaps: filterState.filters.roadmaps,
        gradeLevels: filterState.filters.gradeLevels,
        skillGrades: filterState.filters.skillGrades,
      }),
    [
      filterState.filters.classes,
      filterState.filters.roadmaps,
      filterState.filters.gradeLevels,
      filterState.filters.skillGrades,
    ],
  );
  useEffect(() => {
    setIsLoading(true);
  }, [skillReportKey]);

  useEffect(() => {
    setFilterState(prev => ({
      ...prev,
      filters: { ...prev.filters, skillName: searchValue, type: selectType, unit: selectUnit },
      page: 0,
      first: 0,
    }));
  }, [searchValue, selectType, selectUnit]);

  useEffect(() => {
    setSelectType([]);
    setSearchValue('');
    setSelectUnit('');
    setFilterState(prev => ({
      ...prev,
      filters: { ...prev.filters, classes: classIds, roadmaps: roadmapIds, skillName: '', type: [], unit: '' },
      page: 0,
      first: 0,
    }));
  }, [classIds, roadmapIds]);
  const totalStudents = classStudents.length;
  const tableData: RowData[] = useMemo(() => {
    let rows: RowData[] = skillAssessments
      .filter(s => s.skill !== null)
      .map<RowData>(s => {
        const id = s.skill!.id;
        const placed = placedOutMap[id]?.length ?? s.placedOut?.length ?? 0;
        const notPlaced = Math.max(0, totalStudents - placed);
        return {
          skillId: id,
          skillName: s.skill?.name || '',
          type: s.skill?.displayType || '',
          unit: s.skill?.displayUnit || '',
          moreCount: s.skill?.moreCount ?? 0,
          allUnitsByType: s.skill?.allUnitsByType,
          PLACED_OUT: placed,
          NOT_PLACED_OUT: notPlaced,
        };
      });

    const q = (filterState.filters.skillName ?? '').trim().toLowerCase();
    if (q) rows = rows.filter(r => r.skillName.toLowerCase().includes(q));

    const tArr = Array.isArray(filterState.filters.type) ? (filterState.filters.type as string[]) : [];
    if (tArr.length) {
      const setLower = new Set(tArr.map(s => s.trim().toLowerCase()));
      rows = rows.filter(r => setLower.has((r.type ?? '').trim().toLowerCase()));
    }

    const u = (filterState.filters.unit ?? '').trim().toLowerCase();
    if (u) rows = rows.filter(r => (r.unit ?? '').trim().toLowerCase() === u);

    const key = filterState.sortField || 'skillName';
    const dir = filterState.sortOrder === 1 ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });

    return rows;
  }, [
    skillAssessments,
    placedOutMap,
    totalStudents,
    filterState.filters.skillName,
    filterState.filters.type,
    filterState.filters.unit,
    filterState.sortField,
    filterState.sortOrder,
  ]);
  useEffect(() => {
    onTableData?.(tableData);
  }, [tableData, onTableData]);
  const handleSortChange = (e: DataTableSortEvent) => {
    const field = (e.sortField as string) || 'skillName';

    setFilterState(prev => ({
      ...prev,
      sortField: field,
      sortOrder: (typeof e.sortOrder === 'number' ? e.sortOrder || 1 : 1) as 1 | -1,
      page: 0,
      first: 0,
    }));
  };

  const handlePageChange = (e: DataTablePageEvent) => {
    setFilterState(prev => ({ ...prev, page: e.page ?? 0, first: e.first ?? 0, rows: e.rows ?? prev.rows }));
  };
  const buildMenuItems = (list: PlacedOut[]): MenuItem[] => {
    const sorted = [...list].sort(sortByLastThenFirst);
    return [
      {
        label: 'Copy list of students',
        icon: 'nc_icon-nc_copy icon-sm',
        disabled: sorted.length === 0,
        command: () => {
          const text = sorted.map(s => `${s.firstName} ${s.lastName}`).join(', ');
          navigator.clipboard.writeText(text);
        },
      },
      ...(sorted.length
        ? sorted.map<MenuItem>(s => ({
            label: `${s.firstName} ${s.lastName}`,
            // This label will end up in an aria-label attribute so we have to
            // hide the student elements
            className: 'text-800 ph-no-capture',
          }))
        : [{ label: 'No Students', disabled: true }]),
    ];
  };
  const openPlacedOutMenu = (event: React.MouseEvent, row: RowData) => {
    setMenuItems(buildMenuItems(placedOutMap[row.skillId] ?? []));
    menuRef.current?.toggle(event);
  };
  const openNotPlacedOutMenu = (event: React.MouseEvent, row: RowData) => {
    const placedIds = new Set((placedOutMap[row.skillId] ?? []).map(s => s.id));
    const list = classStudents.filter(s => !placedIds.has(s.id));
    setMenuItems(buildMenuItems(list));
    menuRef.current?.toggle(event);
  };
  const placedOutBody = (row: RowData) => (
    <Button link className="px-2 py-1 border-round-xl" onClick={e => openPlacedOutMenu(e, row)}>
      {row.PLACED_OUT}
    </Button>
  );
  const notPlacedOutBody = (row: RowData) => (
    <Button link className="px-2 py-1 border-round-xl" onClick={e => openNotPlacedOutMenu(e, row)}>
      {row.NOT_PLACED_OUT}
    </Button>
  );
  const typeBody = (row: RowData) => <span className="text-900">{row.type || '—'}</span>;
  const PRIORITY_MULTI = ['Target', 'Support'] as const;
  const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));
  const getAllUnitsOrdered = (row: RowData): string[] => {
    const byType = row.allUnitsByType ?? {};
    const types = Object.keys(byType);
    const head = row.type ? [row.type] : [];
    const tailPriority = PRIORITY_MULTI.filter(t => t !== row.type && types.includes(t as string));
    const rest = types.filter(t => ![row.type, ...tailPriority].includes(t)).sort((a, b) => a.localeCompare(b));
    const order = [...head, ...tailPriority, ...rest];
    const collected: string[] = [];
    order.forEach(t => (byType[t] ?? []).forEach(u => collected.push(u)));

    return uniq(collected);
  };
  const unitBody = (row: RowData) => {
    const allUnits = getAllUnitsOrdered(row);
    const first = row.unit || allUnits[0] || '';
    const more = Math.max(0, allUnits.length - (first ? 1 : 0));
    const openUnitsOverlay = (e: React.MouseEvent) => {
      setOverlayUnits(allUnits);
      unitOverlayRef.current?.toggle(e);
    };
    return (
      <div className="flex align-items-center gap-2">
        <span className="text-900">{first || '—'}</span>
        {more > 0 && (
          <Tag
            className="surface-0 border tw-border-2 tw-border-zinc-950 tw-bg-black text-700"
            value={`+ ${more} more unit${more > 1 ? 's' : ''}`}
            onClick={openUnitsOverlay}
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
    );
  };

  const unitOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of skillAssessments) {
      const u = s.skill?.displayUnit?.trim();
      if (u) set.add(u);
    }
    for (const s of skillAssessments) {
      const map = s.skill?.allUnitsByType;
      if (map) {
        Object.values(map).forEach(arr => {
          (arr ?? []).forEach(u => {
            const unit = (u ?? '').trim();
            if (unit) set.add(unit);
          });
        });
      }
    }
    const uniques = Array.from(set).sort((a, b) => a.localeCompare(b));
    return [...uniques.map(u => ({ label: u, value: u }))];
  }, [skillAssessments]);

  const typeOptions = useMemo(() => {
    const PRIORITY = ['Target', 'Support'] as const;
    const set = new Set<string>();
    skillAssessments.forEach(s => {
      const t = s.skill?.displayType?.trim();
      if (t) set.add(t);
    });
    const uniques = Array.from(set);
    const priorityPart = PRIORITY.filter(p => uniques.includes(p));
    const prioritySet = new Set<string>(priorityPart as readonly string[]);
    const rest = uniques.filter(t => !prioritySet.has(t)).sort((a, b) => a.localeCompare(b));
    return [...[...priorityPart, ...rest].map(t => ({ label: t, value: t }))];
  }, [skillAssessments]);

  return (
    <>
      <Suspense>
        <DiagnosticReportByClass
          key={skillReportKey}
          filterState={filterState}
          setSkillAssessments={setSkillAssessments}
          setPlacedOutMap={setPlacedOutMap}
          setClassStudents={setClassStudents}
          setLoading={setIsLoading}
        />
      </Suspense>
      <OverlayPanel ref={unitOverlayRef} className="p-0 bg-gray-900">
        <div className="p-2 border-round text-0" style={{ minWidth: 300 }}>
          {overlayUnits.length ? (
            overlayUnits.map(u => (
              <div key={u} className="py-1 px-2">
                {u}
              </div>
            ))
          ) : (
            <div className="py-1 px-2 text-300">No units</div>
          )}
        </div>
      </OverlayPanel>
      <TieredMenu model={menuItems} popup ref={menuRef} className="mt-2 dg-no-shadow" />

      <DataTable
        value={tableData}
        totalRecords={tableData.length}
        paginator
        rows={filterState.rows}
        rowsPerPageOptions={[10, 15, 25, 50]}
        showGridlines
        first={filterState.first}
        stripedRows
        emptyMessage={isLoading ? '' : 'No skills found.'}
        loading={isLoading}
        scrollable
        scrollHeight="600px"
        className="w-full"
        onPage={handlePageChange}
        onSort={handleSortChange}
        sortField={filterState.sortField}
        sortOrder={filterState.sortOrder}
        removableSort
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        filterDisplay="row"
      >
        <Column
          field="skillName"
          header="Skill"
          sortable
          filter
          showFilterMenu={false}
          filterField="skillName"
          filterElement={
            <InputText
              id="dgSkillSearch"
              placeholder="Search name"
              className="w-full"
              value={filterState.filters.skillName}
              onChange={e => {
                setSearchValue(e.target.value);
              }}
            />
          }
        />
        <Column
          field="type"
          header="Type"
          filterField="type"
          filter
          sortable
          body={typeBody}
          showFilterMenu={false}
          filterElement={
            <MultiSelect
              id="dgTypeSearch"
              className="w-full"
              placeholder="Select"
              display="chip"
              value={selectType}
              options={typeOptions}
              optionLabel="label"
              optionValue="value"
              filter
              maxSelectedLabels={1}
              onChange={(e: MultiSelectChangeEvent) => {
                setSelectType((e.value as string[]) ?? []);
              }}
              disabled={typeOptions.length <= 1}
            />
          }
        />
        <Column
          field="unit"
          header="Unit"
          sortable
          body={unitBody}
          filter
          showFilterMenu={false}
          filterField="unit"
          filterElement={
            <Dropdown
              id="dgUnitSearch"
              optionLabel="label"
              className="w-full"
              placeholder="Select"
              showClear
              value={filterState.filters.unit || ''}
              options={unitOptions}
              filter
              onChange={(e: DropdownChangeEvent) => {
                setSelectUnit((e.value as string) ?? '');
              }}
              disabled={unitOptions.length <= 1}
            />
          }
        />
        <Column
          field="NOT_PLACED_OUT"
          header="Not Placed Out"
          sortable
          body={notPlacedOutBody}
          className="text-center"
        />
        <Column field="PLACED_OUT" header="Placed Out" sortable body={placedOutBody} className="text-center" />
      </DataTable>
    </>
  );
};
